<template>
    <ds-modal
        class="settings-modal"
        :is-open="isOpen"
        show-close
        center
        :title="$options.MESSAGE_CONSTANTS.TITLE"
        intercept-close
        @close="close"
    >
        <section class="sms-broadcast-settings-modal">
            <span class="label">{{ $options.MESSAGE_CONSTANTS.OPT_IN_LABEL }}</span>
            <p>
                {{ $options.MESSAGE_CONSTANTS.OPT_IN_MESSAGE }}
            </p>
            <ds-text-area-field
                v-model="smsIntro"
                :label="$options.MESSAGE_CONSTANTS.INTRO"
                class="intro"
                data-qa="opt-in-message-intro"
                :maxlength="optInMessageLenghth"
                auto-grow
                required
            >
                <template #help>
                    <div class="character-count">
                        {{ editableCharacterCount }}
                    </div>
                </template>
            </ds-text-area-field>
            <div v-if="showComplianceTip" class="company-name-tip">
                <span class="company-name-tip-message">{{ $options.MESSAGE_CONSTANTS.COMPANY_NAME_TIP }}</span>
                <ds-icon-button name="x" class="company-name-tip-close" @click="complianceTipAcknowledged = true" />
            </div>
            <ds-text-area-field
                :value="optInMessage"
                :label="$options.MESSAGE_CONSTANTS.REQUIRED_LABEL"
                :maxlength="$options.MAX_CHARACTER_COUNT"
                required
                readonly
            >
                <template #help>
                    <div class="character-count">
                        {{ optInCharacterCount }}
                    </div>
                </template>
            </ds-text-area-field>
            <span class="label">{{ $options.MESSAGE_CONSTANTS.NO_REPLY_LABEL }}</span>
            <p>
                {{ $options.MESSAGE_CONSTANTS.NO_REPLY_MESSAGE }}
            </p>
            <ds-text-area-field
                v-model="smsNoReply"
                class="no-reply-area"
                data-qa="no-reply-message"
                :label="$options.MESSAGE_CONSTANTS.NO_REPLY_PLACEHOLDER"
                :maxlength="$options.MAX_CHARACTER_COUNT"
                required
            >
                <template #help>
                    <div class="no-reply-character-count character-count">
                        <p>{{ noReplyCharacterCount }}</p>
                    </div>
                </template>
            </ds-text-area-field>
            <div>
                <ds-inline-alert
                    class="tip-info"
                    leading-icon
                    type="info"
                    data-qa="no-reply-tip"
                >
                    {{ $options.MESSAGE_CONSTANTS.TIP }}
                </ds-inline-alert>
                <ds-filled-button
                    :loading="loading"
                    :disabled="!allowSave"
                    data-qa="save-button"
                    @click="save"
                >
                    {{ $options.MESSAGE_CONSTANTS.SAVE }}
                </ds-filled-button>
            </div>
        </section>
    </ds-modal>
</template>

<script>
import { MAX_CHARACTER_COUNT } from '../constants/smsBroadcast.constants';
import {
    getSmsAccount, updateSmsAccount,
} from '@/api/broadcasts/smsBroadcasts';

export const MESSAGE_CONSTANTS = {
    SAVE: 'Save',
    TITLE: 'Marketing Number Settings',
    INTRO: 'Intro',
    TIP: 'Tip: Consider adding an alternate phone number or email address here so you can communicate in a 1:1 channel.',
    REQUIRED_LABEL: 'Required Info',
    OPT_IN_LABEL: 'Global opt-in message',
    OPT_IN_MESSAGE: `Contacts will receive this the first time you send a marketing text from your
        Keap Marketing Number.  They must reply YES to receive more messages from you.`,
    OPT_IN_TEXT: 'Reply YES to receive texts, and STOP to deny this request. MSG and data rates may apply.',
    NO_REPLY_PLACEHOLDER: 'No-reply message',
    NO_REPLY_LABEL: 'Global no-reply message',
    NO_REPLY_MESSAGE: 'Contacts will receive this message if they reply to a text sent from your Keap Marketing Number.',
    SUCCESS_MESSAGE: 'Your Keap Marketing Number Settings have been updated',
    NO_ACCESS: 'Unable to get account info',
    FAILED_UPDATE: 'There was an error updating your Keap Marketing Number Settings',
    COMPANY_NAME_TIP: 'The law says you must identify who this message is from.\nMake sure you are identifying yourself or your business.',
};

export default {

    MESSAGE_CONSTANTS,
    MAX_CHARACTER_COUNT,
    getSmsAccount,
    updateSmsAccount,

    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
    },

    data () {
        return {
            loading: false,
            isSpaApp: false,
            complianceTipAcknowledged: false,
            smsIntro: '',
            optInMessage: MESSAGE_CONSTANTS.OPT_IN_TEXT,
            smsNoReply: '',
            companyName: '',
        };
    },

    watch: {
        isOpen(newValue) {
            if (newValue) {
                this.onOpen();
            }
        },
    },

    computed: {
        allowSave() {
            return !!this.smsIntro && !!this.smsNoReply;
        },

        containsCompanyName() {
            if (!this.smsIntro || !this.companyName) return false;

            return this.smsIntro.toLowerCase().includes(this.companyName.toLowerCase());
        },

        showComplianceTip() {
            return !(this.containsCompanyName || this.complianceTipAcknowledged);
        },

        editableCharacterCount() {
            return `${this.smsIntro.length}/${MAX_CHARACTER_COUNT - this.optInMessage.length}`;
        },

        optInCharacterCount() {
            return `${this.optInMessage.length + this.smsIntro.length}/${MAX_CHARACTER_COUNT}`;
        },

        noReplyCharacterCount() {
            return `${this.smsNoReply.length}/${MAX_CHARACTER_COUNT}`;
        },

        optInMessageLenghth() {
            if (this.optInMessage) {
                return MAX_CHARACTER_COUNT - this.optInMessage.length;
            }

            return MAX_CHARACTER_COUNT;
        },
    },

    methods: {
        async onOpen() {
            this.loading = true;
            const vm = this;

            getSmsAccount()
                .then((response) => {
                    vm.companyName = response.companyName;
                    vm.smsIntro = response.optInMessageIntro;
                    vm.smsNoReply = response.replyMessage;
                    this.loading = false;
                })
                .catch(() => {
                    this.$error({ message: MESSAGE_CONSTANTS.NO_ACCESS });
                });
        },

        async save() {
            this.loading = true;
            const payload = {
                optInMessageIntro: this.smsIntro,
                replyMessage: this.smsNoReply,
            };

            updateSmsAccount(payload)
                .then(() => {
                    this.$toast({ message: MESSAGE_CONSTANTS.SUCCESS_MESSAGE });
                    this.close();
                })
                .catch(() => {
                    this.$error({ message: MESSAGE_CONSTANTS.FAILED_UPDATE });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        close() {
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss">
    div.settings-modal div.modal-header{
        padding: 0;
    }

    div.settings-modal div.modal-body {
        padding: 0;
    }

    div.settings-modal section.sms-broadcast-settings-modal {
        display: inline-grid;
        grid-template-columns: 100%;
        grid-gap: 0px;
        margin: 1.5rem;
    }

    div.settings-modal section.sms-broadcast-settings-modal > span.label {
        font-weight: 500;
        margin-bottom: .5rem;
        font-size: 1.25rem;
    }

    div.settings-modal section.sms-broadcast-settings-modal > p{
        font-size: .875rem;
        color: $color-ink-800;
        margin-bottom: 1rem;
    }

    div.settings-modal section.sms-broadcast-settings-modal > span.tip{
        line-height: 1.25rem;
        margin: 0 0 1rem;
        color: $color-ink-700;
    }

    div.settings-modal section.sms-broadcast-settings-modal > .intro{
        margin-bottom: 1rem;
    }

    div.settings-modal section.sms-broadcast-settings-modal > div.intro > textarea{
        min-height: 62px;
    }

    div.settings-modal section.sms-broadcast-settings-modal > div.no-reply-character-count {
        display: flex;
        justify-content: space-between;
    }

    div.settings-modal section.sms-broadcast-settings-modal > .input-field {
        margin-bottom: .2rem;
    }

    div.settings-modal .company-name-tip {
        background-color: $color-forest-050;
        border-radius: .375rem;
        margin-bottom: 1.5rem;
        display: inline-grid;
        grid-template-rows: 80px;
        grid-template-columns: 90% 10%;
        justify-items: center;
        align-items: center;
    }

    div.settings-modal .company-name-tip-message {
        padding: 1rem;
        font-size: 1rem;
        line-height: 1.5rem;
    }

    div.settings-modal div.status {
        display: none;
    }

    div.settings-modal .character-count,
    div.settings-modal .character-count > p{
        float: right;
        font-size: 0.75rem;
    }

    div.settings-modal .no-reply-character-count {
        display: inline-grid;
        justify-items: end;
        width: 100%;
        height: 1rem;
    }

    div.settings-modal div.modal-header.has-navigation {
        padding: 0;
    }

    div.settings-modal div.modal-header section.text {
        padding-top: .3rem;
    }

    div.settings-modal div.modal-body-inner {
        padding: 0;
    }

    div.settings-modal div.modal-body-inner div.no-reply-area > textarea {
        min-height: 4rem;
    }

    div.settings-modal div.modal-body-inner div.tip-info {
        margin-bottom: 1.5rem;
    }
</style>
